import { View } from 'foundations-components/components/View';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { useCallback, useEffect, useRef, useState } from 'react';
import CitationCard from './CitationCard';
import CitationPill from './CitationPill';
import { jsx as _jsx } from "react/jsx-runtime";
const InlineCitation = ({
  // CitationPill props
  label,
  onPillClick,
  // CitationCard props
  title,
  locationText,
  sourceName,
  icon,
  badges,
  summary,
  feedbackComponent,
  onSourceClick,
  // Popover props
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  placement = 'right',
  closeOnOutsideClick = true,
  // Layout props
  maxBlockSize,
  // TEMPORARY solution for INBOUND
  // We need a way to determine if a citation is a CRM object
  // Consider passing the entire citation object to handle conditional rendering based on Citationn type
  isCrmObjectCitation = false,
  // Tracking callback
  onCitationOpened
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const onCitationOpenedRef = useRef(onCitationOpened);
  useEffect(() => {
    onCitationOpenedRef.current = onCitationOpened;
  }, [onCitationOpened]);

  // Timeout to delay closing the popover (100ms) when mouse leaves the pill,
  // giving users time to move their cursor to the popover content
  const closeDelayTimeoutRef = useRef(null);

  // Timeout to delay opening the popover (200ms) when hovering over the pill,
  // preventing accidental opens from quick mouse movements
  const openDelayTimeoutRef = useRef(null);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;

  // Track when popover opens (from any source: hover, controlled state, etc.)
  // Initialize to false to detect opens on initial mount
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (isOpen && !wasOpen) {
      var _onCitationOpenedRef$;
      (_onCitationOpenedRef$ = onCitationOpenedRef.current) === null || _onCitationOpenedRef$ === void 0 || _onCitationOpenedRef$.call(onCitationOpenedRef);
    }
  }, [isOpen]);
  const clearCloseDelayTimeout = useCallback(() => {
    if (closeDelayTimeoutRef.current) {
      clearTimeout(closeDelayTimeoutRef.current);
      closeDelayTimeoutRef.current = null;
    }
  }, []);
  const clearOpenDelayTimeout = useCallback(() => {
    if (openDelayTimeoutRef.current) {
      clearTimeout(openDelayTimeoutRef.current);
      openDelayTimeoutRef.current = null;
    }
  }, []);
  const handlePillMouseEnter = useCallback(() => {
    if (isSticky) return;
    clearCloseDelayTimeout();
    clearOpenDelayTimeout();
    openDelayTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, 200);
  }, [isSticky, clearCloseDelayTimeout, clearOpenDelayTimeout, setIsOpen]);
  const handlePillMouseLeave = useCallback(() => {
    if (isSticky) return;
    clearOpenDelayTimeout();
    closeDelayTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  }, [isSticky, clearOpenDelayTimeout, setIsOpen]);
  const handlePopoverMouseEnter = useCallback(() => {
    if (isSticky) return;
    clearCloseDelayTimeout();
  }, [isSticky, clearCloseDelayTimeout]);
  const handlePopoverMouseLeave = useCallback(() => {
    if (isSticky) return;
    setIsOpen(false);
  }, [isSticky, setIsOpen]);
  const handlePopoverInteraction = useCallback(() => {
    // Switch to sticky mode - now only outside clicks will close
    setIsSticky(true);
    clearCloseDelayTimeout();
  }, [clearCloseDelayTimeout]);
  const handlePopoverClose = useCallback(() => {
    setIsOpen(false);
    setIsSticky(false);
    clearCloseDelayTimeout();
    clearOpenDelayTimeout();
  }, [setIsOpen, clearCloseDelayTimeout, clearOpenDelayTimeout]);
  return /*#__PURE__*/_jsx(Popover, {
    open: isOpen,
    onOpenChange: handlePopoverClose,
    content: /*#__PURE__*/_jsx(View, {
      inlineSize: "360px",
      onMouseEnter: handlePopoverMouseEnter,
      onMouseLeave: handlePopoverMouseLeave,
      onClick: handlePopoverInteraction,
      children: /*#__PURE__*/_jsx(CitationCard, {
        title: title,
        locationText: locationText,
        sourceName: sourceName,
        icon: icon,
        isCrmObjectCitation: isCrmObjectCitation,
        badges: badges,
        summary: summary,
        feedbackComponent: feedbackComponent,
        onClick: onSourceClick,
        maxBlockSize: maxBlockSize
      })
    }),
    closeOnOutsideClick: closeOnOutsideClick,
    placement: placement,
    children: /*#__PURE__*/_jsx(CitationPill, {
      label: label,
      onClick: onPillClick,
      onMouseEnter: handlePillMouseEnter,
      onMouseLeave: handlePillMouseLeave
    })
  });
};
InlineCitation.displayName = 'InlineCitation';
export default InlineCitation;